import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Participant } from "../../common/types/domain";
import { CustomDialog } from "../../common/components/CustomDialog";
import { socketService } from "../../../services/socket";
import { PARTICIPANT_COLORS, getParticipantColor } from "../../../utils/participantColors";
import { ParticipantBadge } from "../../common/components/ParticipantBadge";
import { CustomSelect } from "../../common/components/CustomSelect";
import { ImageCropperModal } from "../../common/components/ImageCropperModal";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import config from "../../../config";
import { supabase } from "../../../services/supabase";

const CustomRoleSelect = ({
  value,
  options,
  onChange,
  onManage,
}: {
  value: string;
  options: { id: string; name: string }[];
  onChange: (v: string) => void;
  onManage: () => void;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Fallback if options empty
  const safeOptions = options.length > 0 ? options : [{ id: "def", name: "contestant" }];

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-auto py-[10px] px-[10px] rounded-[6px] font-bold uppercase text-[0.9rem] flex justify-between items-center cursor-pointer select-none"
        style={{
          background: "var(--color-input-bg)",
          border: "1px solid var(--color-panel-border)",
          color: "rgba(255, 255, 255, 0.8)",
        }}
      >
        <span>{value || t("common.participantBadge.selectRole")}</span>
        <span
          style={{
            fontSize: "0.7rem",
            opacity: 0.7,
            transform: isOpen ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        >
          ▼
        </span>
      </div>
      {isOpen && (
        <>
          <div
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              top: "110%",
              left: 0,
              right: 0,
              background: "var(--color-panel-bg)",
              border: "1px solid var(--color-panel-border)",
              borderRadius: "6px",
              zIndex: 100,
              boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
              overflow: "hidden",
              backdropFilter: "blur(10px)",
            }}
          >
            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
              {safeOptions.map((r) => (
                <div
                  key={r.id}
                  onClick={() => {
                    onChange(r.name);
                    setIsOpen(false);
                  }}
                  style={{
                    padding: "12px",
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                    cursor: "pointer",
                    background: value === r.name ? "rgba(45, 111, 247, 0.15)" : "transparent",
                    color: value === r.name ? "var(--color-accent-primary)" : "#ccc",
                    textTransform: "uppercase",
                    fontSize: "0.85rem",
                    fontWeight: value === r.name ? "bold" : "normal",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background =
                      value === r.name ? "rgba(45, 111, 247, 0.25)" : "rgba(255,255,255,0.05)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      value === r.name ? "rgba(45, 111, 247, 0.15)" : "transparent")
                  }
                >
                  {r.name}
                </div>
              ))}
            </div>
            <div
              onClick={() => {
                onManage();
                setIsOpen(false);
              }}
              style={{
                padding: "12px",
                background: "rgba(0,0,0,0.3)",
                borderTop: "1px solid var(--color-panel-border)",
                cursor: "pointer",
                color: "var(--color-text-muted)",
                fontSize: "0.75rem",
                display: "flex",
                gap: "8px",
                alignItems: "center",
                justifyContent: "center",
                letterSpacing: "1px",
                fontWeight: "bold",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(0,0,0,0.3)";
                e.currentTarget.style.color = "var(--color-text-muted)";
              }}
            >
              <span>⚙️</span> {t("controller.participants.manageRoles")}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface ParticipantsEditorProps {
  externalParticipants?: Participant[];
  externalRoles?: { id: string; name: string }[];
  externalGroups?: { id: string; name: string; color: string }[];
}

export const ParticipantsEditor: React.FC<ParticipantsEditorProps> = ({
  externalParticipants = [],
  externalRoles = [],
  externalGroups = [],
}) => {
  const { t } = useTranslation();
  const participants = externalParticipants;
  const availableRoles = externalRoles;
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form State for Adding / Editing
  const [formCode, setFormCode] = useState("");
  const [formAlias, setFormAlias] = useState("");
  const [formName, setFormName] = useState("");
  const [formNumber, setFormNumber] = useState<number>(0);
  const [formRole, setFormRole] = useState("contestant");
  const [formImage, setFormImage] = useState("");
  const [formAvatarUrl, setFormAvatarUrl] = useState("");
  const [formBiography, setFormBiography] = useState("");
  const [formGroupId, setFormGroupId] = useState<string | null>(null);
  const [formColorId, setFormColorId] = useState<string>("fallback");
  const [formCustomColor, setFormCustomColor] = useState<string>("#ffffff");
  const [formStatus, setFormStatus] = useState<Participant["status"]>("available");
  const [isAdding, setIsAdding] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  // Voice enrollment (Speechmatics)
  const [voiceStatus, setVoiceStatus] = useState<Participant["voiceStatus"]>(null);
  const [voiceCount, setVoiceCount] = useState<number>(0);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  // Roles State
  const [showRoleManager, setShowRoleManager] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");

  // Hover Profile State
  const [hoveredParticipant, setHoveredParticipant] = useState<Participant | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "alert" | "prompt" | "confirm";
    onConfirm: (val?: string) => void;
    defaultValue?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "alert",
    onConfirm: () => {},
  });

  // --- SOCKET CONNECTION ---
  useEffect(() => {
    socketService.connect();
    // Force refresh if we have no data
    if (externalParticipants.length === 0) {
      socketService.emit("register-editor");
      socketService.emit("get-init-data");
    }
  }, [externalParticipants.length]);

  const resetForm = () => {
    setFormCode("");
    setFormAlias("");
    setFormName("");
    setFormNumber(participants.length + 1);
    setFormRole("contestant");
    setFormImage("");
    setFormAvatarUrl("");
    setFormBiography("");
    setFormGroupId(null);
    setFormColorId("fallback");
    setFormCustomColor("#ffffff");
    setFormStatus("available");
    setVoiceStatus(null);
    setVoiceCount(0);
    setVoiceUploading(false);
    setVoiceError(null);
  };

  const [_isProcessingImage, setIsProcessingImage] = useState(false);

  const secureResizeToDataURL = async (file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const imgUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = async () => {
        // Pequeño respiro para el main thread
        await new Promise((r) => setTimeout(r, 10));

        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;
        const MAX_SAFE_SIZE = 1200;

        if (width > height) {
          if (width > MAX_SAFE_SIZE) {
            height *= MAX_SAFE_SIZE / width;
            width = MAX_SAFE_SIZE;
          }
        } else {
          if (height > MAX_SAFE_SIZE) {
            width *= MAX_SAFE_SIZE / height;
            height = MAX_SAFE_SIZE;
          }
        }

        canvas.width = Math.round(width);
        canvas.height = Math.round(height);
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const result = canvas.toDataURL("image/jpeg", 0.8);
          URL.revokeObjectURL(imgUrl); // Liberar memoria RAM
          resolve(result);
        } else {
          URL.revokeObjectURL(imgUrl);
          reject(new Error("No 2d context"));
        }
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(imgUrl);
        reject(e);
      };
      img.src = imgUrl;
    });
  };

  // Allowed image domains for external URL processing
  const ALLOWED_IMAGE_DOMAINS = [
    "kaoqulorqagqfgrvpcdm.supabase.co",
    "lh3.googleusercontent.com",
    "images.unsplash.com",
    "i.imgur.com",
  ];

  const processExternalImageUrl = async (urlStr: string) => {
    try {
      const parsed = new URL(urlStr);
      if (
        !ALLOWED_IMAGE_DOMAINS.some(
          (d) => parsed.hostname === d || parsed.hostname.endsWith("." + d),
        )
      ) {
        throw new Error(
          `Domain not allowed: ${parsed.hostname}. Upload the image directly instead.`,
        );
      }
      const response = await fetch(urlStr);
      if (!response.ok) throw new Error("HTTP error " + response.status);
      const blob = await response.blob();
      const secureDataUrl = await secureResizeToDataURL(blob);
      setCropImageSrc(secureDataUrl);
    } catch (err: any) {
      console.warn("Image URL processing failed:", err?.message || err);
      // Don't fall back to raw URL — require upload instead
      alert(err?.message || "Could not load image from URL. Please upload the image directly.");
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsProcessingImage(true);
      // Dar 50ms al Main Thread para que renderice el loader antes de colgarse con FileReader
      await new Promise((r) => setTimeout(r, 50));
      try {
        const secureDataUrl = await secureResizeToDataURL(file);
        setCropImageSrc(secureDataUrl);
      } catch (err) {
        console.error("Error reading image upload", err);
      } finally {
        setIsProcessingImage(false);
      }
    }
  };

  const handleDropImage = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImage(false);

    // 1. Intentar atrapar el File si viene en drag and drop real de SO
    let file: File | null = null;
    if (e.dataTransfer.items) {
      const fileItem = Array.from(e.dataTransfer.items).find(
        (item) => item.kind === "file" && item.type.startsWith("image/"),
      );
      if (fileItem) file = fileItem.getAsFile();
    } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      file = e.dataTransfer.files[0];
    }

    if (file && file.type.startsWith("image/")) {
      setIsProcessingImage(true);
      await new Promise((r) => setTimeout(r, 50));
      try {
        const secureDataUrl = await secureResizeToDataURL(file);
        setCropImageSrc(secureDataUrl);
      } catch (err) {
        console.error("Error reading dropped image", err);
      } finally {
        setIsProcessingImage(false);
      }
    } else {
      // Drag and Drop desde buscador (ej. Safari/Chrome / enlace)
      let url =
        e.dataTransfer.getData("URL") ||
        e.dataTransfer.getData("text/uri-list") ||
        e.dataTransfer.getData("text/plain");

      // Safari/Chrome a veces envían la imagen copiando el nodo HTML <img src="...">
      const htmlData = e.dataTransfer.getData("text/html");
      if (htmlData) {
        // Regex to find src="..." or src='...'
        const match = htmlData.match(/src\s*=\s*(['"])(.*?)\1/);
        if (match && match[2]) {
          url = match[2];
        }
      }

      // Fallback si a pesar de todo no encontró nada por las rutas oficiales, buscar en todos los items
      if (!url && e.dataTransfer.items) {
        for (let i = 0; i < e.dataTransfer.items.length; i++) {
          const item = e.dataTransfer.items[i];
          if (item.kind === "string" && item.type === "text/uri-list") {
            item.getAsString((str) => processExternalImageUrl(str));
            return; // Done
          }
        }
      }

      // Fix for relative URLs
      if (url && url.startsWith("/")) {
        // Cannot use relative URLs easily across different domains, but we try
        url = window.location.origin + url;
      }

      if (url) {
        processExternalImageUrl(url);
      } else {
        console.warn(
          "[ParticipantsEditor] Dragged item was not a recognizable image or URL",
          e.dataTransfer.types,
        );
      }
    }
  };

  const formRef = React.useRef<HTMLDivElement>(null);

  const handleAddClick = () => {
    setIsAdding(true);
    setEditingId(null);
    resetForm();
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const handleEdit = (p: Participant) => {
    setIsAdding(false);
    setEditingId(p.id);
    setFormCode(p.code || "");
    setFormAlias(p.alias || "");
    setFormName(p.name);
    setFormNumber(p.number || 0);
    setFormRole(p.role || "contestant");
    setFormImage(p.image || "");
    setFormAvatarUrl(p.avatarUrl || "");
    setFormBiography(p.biography || "");
    setFormGroupId(p.groupId || null);
    setFormColorId(p.metadata?.colorId || "fallback");
    setFormCustomColor(p.metadata?.customColor || "#ffffff");
    setFormStatus(p.status || "available");
    setVoiceStatus(p.voiceStatus || null);
    setVoiceCount(p.speakerIdentifiers?.length || 0);
    setVoiceUploading(false);
    setVoiceError(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const handleSave = () => {
    const commonData = {
      code: formCode.toUpperCase(),
      alias: formAlias.toUpperCase(),
      name: formName,
      number: formNumber,
      role: formRole,
      image: formImage,
      avatarUrl: formAvatarUrl,
      biography: formBiography,
      groupId: formGroupId,
      status: formStatus,
      metadata: { colorId: formColorId, customColor: formCustomColor },
    };

    if (isAdding) {
      const newParticipant: Participant = {
        id: crypto.randomUUID(),
        ...commonData,
        type: "talent",
        status: "available",
      };
      // Send only the new participant — server upserts and broadcasts canonical list
      socketService.emit("upsert-participant", newParticipant);
    } else {
      const edited = participants.find((p) => p.id === editingId);
      if (edited) {
        // Send only the edited participant
        socketService.emit("upsert-participant", { ...edited, ...commonData });
      }
    }

    setEditingId(null);
    setIsAdding(false);
    resetForm();
  };

  const handleDelete = (id: string, name: string) => {
    setDialog({
      isOpen: true,
      title: t("controller.participants.removeParticipant"),
      message: t("controller.participants.removeParticipantConfirm", { name }),
      type: "confirm",
      onConfirm: () => {
        // Use explicit delete event — server will remove from DB and broadcast updated canonical list
        socketService.emit("delete-participant", id);
        setDialog((d) => ({ ...d, isOpen: false }));
      },
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
  };

  const handleVoiceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permitir re-subir el mismo archivo
    if (!file || !editingId) return;

    setVoiceUploading(true);
    setVoiceStatus("PROCESSING");
    setVoiceError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(`${config.API_URL}/api/participants/${editingId}/enroll-voice`, {
        method: "POST",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          "X-Filename": file.name,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: file,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || `Error ${res.status}`);
      }
      setVoiceStatus("COMPLETED");
      setVoiceCount(json.speakerIdentifiers?.length || 0);
    } catch (err: any) {
      setVoiceStatus("FAILED");
      setVoiceError(err?.message || "Error al procesar el audio");
    } finally {
      setVoiceUploading(false);
    }
  };

  return (
    <div
      data-testid="participants-editor"
      style={{
        padding: "var(--spacing-lg)",
        background: "var(--color-panel-bg)",
        borderRadius: "var(--border-radius-lg)",
        boxShadow: "var(--shadow-card)",
        border: "1px solid var(--color-panel-border)",
        width: "100%",
        color: "var(--color-text-primary)",
        minHeight: "100%",
      }}
    >
      <div
        data-testid="participants-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #333",
          paddingBottom: "15px",
          marginBottom: "20px",
        }}
      >
        <div>
          <h2
            data-testid="participants-title"
            style={{
              color: "var(--color-role-controller)",
              margin: 0,
              fontSize: "1.2rem",
              fontWeight: "900",
              letterSpacing: "2px",
            }}
          >
            {t("controller.participants.title")}
          </h2>
          <p style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", margin: "5px 0 0 0" }}>
            {t("controller.participants.subtitle")}
          </p>
        </div>
        {!isAdding && (
          <button
            data-testid="add-participant-btn"
            onClick={handleAddClick}
            style={{
              background: "var(--color-accent-success)",
              color: "#000",
              border: "none",
              padding: "10px 20px",
              borderRadius: "8px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>{t("controller.participants.addTalent")}</span>
          </button>
        )}
      </div>

      {(isAdding || editingId) && (
        <div
          ref={formRef}
          data-testid="participant-form"
          style={{
            background: "var(--color-bg-base)",
            border: "1px solid var(--color-panel-border)",
            padding: "20px",
            borderRadius: "6px",
            marginBottom: "25px",
            display: "flex",
            gap: "25px",
            alignItems: "flex-start",
            boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
          }}
        >
          {/* AVATAR UPLOAD & URL */}
          <div
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}
          >
            <div
              style={{
                width: "100px",
                height: "100px",
                aspectRatio: "1 / 1",
                flexShrink: 0,
                borderRadius: "50%",
                background: isDraggingImage
                  ? "var(--color-accent-success)"
                  : getParticipantColor(formColorId, formCustomColor).bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                position: "relative",
                padding: isDraggingImage ? "6px" : "2px", // Thin circular border applied via padding + background
                boxShadow: isDraggingImage
                  ? `0 0 25px var(--color-accent-success), 0 2px 4px rgba(0,0,0,0.3)`
                  : `0 0 15px ${getParticipantColor(formColorId, formCustomColor).bg}44, 0 2px 4px rgba(0,0,0,0.3)`,
                transition: "all 0.2s ease",
              }}
              onClick={() => document.getElementById("avatar-upload")?.click()}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDraggingImage(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDraggingImage(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDraggingImage(false);
              }}
              onDrop={handleDropImage}
              title={t("controller.participants.uploadPhoto")}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  background:
                    formAvatarUrl || formImage
                      ? `url("${formAvatarUrl || formImage}") center/cover no-repeat`
                      : "var(--color-bg-mesh)",
                  boxShadow: "inset 0 0 20px rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {!(formAvatarUrl || formImage) && (
                  <span style={{ fontSize: "2rem", opacity: 0.3 }}>📷</span>
                )}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: "rgba(0,0,0,0.7)",
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "0.55rem",
                    textAlign: "center",
                    padding: "4px",
                    textTransform: "uppercase",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.participants.upload")}
                </div>
              </div>
            </div>
            <input
              id="avatar-upload"
              data-testid="participant-avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
            />

            <div style={{ width: "100%", marginTop: "5px" }}>
              <label
                style={{
                  fontSize: "0.6rem",
                  color: "var(--color-text-muted)",
                  fontWeight: "bold",
                  marginBottom: "2px",
                  display: "block",
                  textAlign: "center",
                }}
              >
                {t("controller.participants.orImageUrl")}
              </label>
              <input
                data-testid="participant-avatar-url"
                value={formAvatarUrl}
                onChange={(e) => setFormAvatarUrl(e.target.value)}
                placeholder={t("controller.participants.urlPlaceholder")}
                style={{
                  background: "var(--color-input-bg)",
                  border: "1px solid var(--color-panel-border)",
                  color: "rgba(255, 255, 255, 0.8)",
                  padding: "6px 8px",
                  borderRadius: "4px",
                  width: "100%",
                  outline: "none",
                  fontSize: "0.75rem",
                  textAlign: "center",
                }}
              />
            </div>

            {/* VOICE ENROLLMENT (Speechmatics) */}
            <div style={{ width: "100%", marginTop: "12px" }}>
              <label
                style={{
                  fontSize: "0.6rem",
                  color: "var(--color-text-muted)",
                  fontWeight: "bold",
                  marginBottom: "4px",
                  display: "block",
                  textAlign: "center",
                }}
              >
                {t("controller.participants.voiceEnrollment", "VOZ (IDENTIFICACIÓN)")}
              </label>

              {editingId ? (
                <>
                  <input
                    id="voice-upload"
                    data-testid="participant-voice-upload"
                    type="file"
                    accept="audio/*"
                    onChange={handleVoiceUpload}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    data-testid="participant-voice-upload-btn"
                    disabled={voiceUploading}
                    onClick={() => document.getElementById("voice-upload")?.click()}
                    style={{
                      width: "100%",
                      background: voiceUploading ? "var(--color-bg-mesh)" : "var(--color-input-bg)",
                      border: "1px solid var(--color-panel-border)",
                      color: "rgba(255, 255, 255, 0.8)",
                      padding: "8px",
                      borderRadius: "6px",
                      cursor: voiceUploading ? "wait" : "pointer",
                      fontSize: "0.72rem",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    <span>🎙️</span>
                    <span>
                      {voiceUploading
                        ? t("controller.participants.voiceProcessing", "Procesando…")
                        : voiceCount > 0
                          ? t("controller.participants.voiceReenroll", "Re-subir audio")
                          : t("controller.participants.voiceUpload", "Subir audio de voz")}
                    </span>
                  </button>

                  <div
                    style={{
                      marginTop: "6px",
                      textAlign: "center",
                      fontSize: "0.62rem",
                      fontWeight: "bold",
                      color:
                        voiceStatus === "COMPLETED"
                          ? "var(--color-accent-success)"
                          : voiceStatus === "FAILED"
                            ? "var(--color-accent-danger)"
                            : "var(--color-text-muted)",
                    }}
                  >
                    {voiceStatus === "COMPLETED" &&
                      t("controller.participants.voiceEnrolled", {
                        defaultValue: "✓ Voz registrada ({{n}})",
                        n: voiceCount,
                      })}
                    {voiceStatus === "PROCESSING" &&
                      t("controller.participants.voiceProcessing", "Procesando…")}
                    {voiceStatus === "FAILED" &&
                      (voiceError || t("controller.participants.voiceFailed", "Falló el proceso"))}
                    {!voiceStatus && t("controller.participants.voiceNone", "Sin voz registrada")}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.62rem",
                    color: "var(--color-text-muted)",
                    fontStyle: "italic",
                    lineHeight: 1.4,
                  }}
                >
                  {t(
                    "controller.participants.voiceSaveFirst",
                    "Guardá el participante para poder subir su audio de voz.",
                  )}
                </div>
              )}
            </div>
          </div>

          {/* FIELDS CONTAINER */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "15px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "80px 100px 150px 1fr",
                gap: "15px",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--color-accent-warning)",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.participants.numberCH")}
                </label>
                <input
                  data-testid="participant-number-input"
                  type="number"
                  value={formNumber}
                  onChange={(e) => setFormNumber(parseInt(e.target.value) || 0)}
                  style={{
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "rgba(255, 255, 255, 0.8)",
                    padding: "10px",
                    borderRadius: "6px",
                    width: "100%",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--color-text-muted)",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.participants.code")}
                </label>
                <input
                  data-testid="participant-code-input"
                  value={formCode}
                  onChange={(e) => setFormCode(e.target.value.toUpperCase().slice(0, 3))}
                  placeholder={t("controller.participants.codePlaceholder")}
                  style={{
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "rgba(255, 255, 255, 0.8)",
                    padding: "10px",
                    borderRadius: "6px",
                    width: "100%",
                    outline: "none",
                    fontFamily: "var(--font-mono)",
                  }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--color-text-muted)",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.participants.alias")}
                </label>
                <input
                  data-testid="participant-alias-input"
                  value={formAlias}
                  onChange={(e) => setFormAlias(e.target.value.toUpperCase())}
                  placeholder={t("controller.participants.aliasPlaceholder")}
                  style={{
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "rgba(255, 255, 255, 0.8)",
                    padding: "10px",
                    borderRadius: "6px",
                    width: "100%",
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--color-text-muted)",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.participants.fullName")}
                </label>
                <input
                  data-testid="participant-name-input"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={t("controller.participants.fullName")}
                  style={{
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "rgba(255, 255, 255, 0.8)",
                    padding: "10px",
                    borderRadius: "6px",
                    width: "100%",
                    outline: "none",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "15px",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--color-text-muted)",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.participants.role")}
                </label>
                <CustomRoleSelect
                  value={formRole}
                  options={availableRoles}
                  onChange={setFormRole}
                  onManage={() => setShowRoleManager(true)}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label
                  style={{
                    fontSize: "0.65rem",
                    color: "var(--color-text-muted)",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.participants.groupTeam")}
                </label>
                <CustomSelect
                  value={formGroupId || ""}
                  onChange={(v) => setFormGroupId(v || null)}
                  options={[
                    { value: "", label: t("controller.participants.noGroup") },
                    ...externalGroups.map((g) => ({ value: g.id, label: g.name })),
                  ]}
                  className="w-full h-auto py-[10px] px-[10px] rounded-[6px] font-bold uppercase text-[0.9rem]"
                  style={{
                    background: "var(--color-input-bg)",
                    border: "1px solid var(--color-panel-border)",
                    color: "rgba(255, 255, 255, 0.8)",
                  }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label
                  style={{
                    fontSize: "0.65rem",
                    color:
                      formStatus === "eliminated"
                        ? "var(--color-accent-danger)"
                        : "var(--color-text-muted)",
                    fontWeight: "bold",
                  }}
                >
                  {t("controller.participants.status")}
                </label>
                <CustomSelect
                  value={formStatus}
                  onChange={(v) => setFormStatus(v as Participant["status"])}
                  options={[
                    { value: "available", label: t("controller.participants.active") },
                    { value: "eliminated", label: t("controller.participants.eliminated") },
                  ]}
                  className={`w-full h-auto py-[10px] px-[10px] rounded-[6px] font-bold uppercase text-[0.9rem]`}
                  style={{
                    background:
                      formStatus === "eliminated"
                        ? "rgba(247, 45, 96, 0.1)"
                        : "var(--color-input-bg)",
                    border:
                      formStatus === "eliminated"
                        ? "1px solid #f72d60"
                        : "1px solid var(--color-panel-border)",
                    color: formStatus === "eliminated" ? "#f72d60" : "rgba(255, 255, 255, 0.8)",
                  }}
                />
              </div>
            </div>

            {/* COLOR */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "5px", marginTop: "10px" }}
            >
              <label
                style={{
                  fontSize: "0.65rem",
                  color: "var(--color-text-muted)",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}
              >
                {t("controller.participants.themeColor")}
              </label>
              <div
                data-testid="participant-color-picker"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  padding: "15px",
                  background: "var(--color-bg-base)",
                  borderRadius: "6px",
                  border: "1px solid var(--color-panel-border)",
                }}
              >
                {Object.values(PARTICIPANT_COLORS).map((color) => (
                  <div
                    key={color.id}
                    onClick={() => setFormColorId(color.id)}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: color.bg,
                      border:
                        formColorId === color.id ? "2px solid white" : `1px solid ${color.border}`,
                      cursor: "pointer",
                      boxShadow:
                        formColorId === color.id ? "0 0 0 3px rgba(45, 111, 247, 0.4)" : "none",
                      transform: formColorId === color.id ? "scale(1.15)" : "scale(1)",
                      transition: "all 0.2s",
                      opacity: color.id.includes("striped") ? 0.8 : 1, // just visual distinction
                    }}
                    title={color.id.replace("_", " ")}
                  />
                ))}
                {/* Custom Color Picker */}
                <Popover>
                  <PopoverTrigger
                    render={
                      <Button
                        variant="outline"
                        className="size-7 p-0 rounded-md border border-[#444] transition-all hover:scale-110 overflow-hidden"
                        style={{
                          background: formColorId === "custom" ? formCustomColor : "transparent",
                          borderColor: formColorId === "custom" ? "white" : "#444",
                          boxShadow:
                            formColorId === "custom" ? "0 0 0 3px rgba(45, 111, 247, 0.4)" : "none",
                          transform: formColorId === "custom" ? "scale(1.15)" : "scale(1)",
                        }}
                        title={t("controller.participants.pickColor")}
                        onClick={() => setFormColorId("custom")}
                      />
                    }
                  >
                    {formColorId !== "custom" && (
                      <div
                        className="size-full"
                        style={{
                          background:
                            "conic-gradient(from 0deg, red, yellow, lime, aqua, blue, magenta, red)",
                        }}
                      />
                    )}
                  </PopoverTrigger>
                  <PopoverContent className="w-56" align="start">
                    <div className="flex flex-col gap-3">
                      <div className="text-sm font-medium">
                        {t("controller.participants.pickColor")}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={formCustomColor}
                          onChange={(e) => {
                            setFormColorId("custom");
                            setFormCustomColor(e.target.value);
                          }}
                          className="size-10 p-1 cursor-pointer"
                        />
                        <Input
                          type="text"
                          value={formCustomColor}
                          onChange={(e) => {
                            setFormColorId("custom");
                            setFormCustomColor(e.target.value);
                          }}
                          className="flex-1 font-mono uppercase"
                          maxLength={7}
                        />
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* BIOGRAPHY */}
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <label
                style={{
                  fontSize: "0.65rem",
                  color: "var(--color-text-muted)",
                  fontWeight: "bold",
                }}
              >
                {t("controller.participants.biography")}
              </label>
              <textarea
                data-testid="participant-biography-input"
                value={formBiography}
                onChange={(e) => setFormBiography(e.target.value)}
                rows={3}
                style={{
                  background: "var(--color-input-bg)",
                  border: "1px solid var(--color-panel-border)",
                  color: "rgba(255, 255, 255, 0.8)",
                  padding: "10px",
                  borderRadius: "6px",
                  width: "100%",
                  outline: "none",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
                placeholder={t("controller.participants.biographyPlaceholder")}
              />
            </div>

            <div
              style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "5px" }}
            >
              <button
                data-testid="participant-save-btn"
                onClick={handleSave}
                style={{
                  minWidth: "120px",
                  background: "var(--color-accent-primary)",
                  color: "rgba(255, 255, 255, 0.8)",
                  border: "none",
                  padding: "12px",
                  borderRadius: "6px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 12px rgba(45, 111, 247, 0.3)",
                }}
              >
                {isAdding
                  ? t("controller.participants.addTalent")
                  : t("controller.participants.saveChanges")}
              </button>
              <button
                data-testid="participant-cancel-btn"
                onClick={handleCancel}
                style={{
                  background: "var(--color-bg-mesh)",
                  color: "var(--color-text-muted)",
                  border: "1px solid var(--color-panel-border)",
                  padding: "12px 20px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                {t("controller.participants.cancelEdit")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div data-testid="participants-table-container" style={{ overflowX: "auto" }}>
        <table
          data-testid="participants-table"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr
              style={{
                color: "var(--color-text-muted)",
                fontSize: "0.7rem",
                textTransform: "uppercase",
                textAlign: "left",
                borderBottom: "1px solid #222",
              }}
            >
              <th style={{ padding: "15px" }}>{t("controller.participants.tableNumber")}</th>
              <th style={{ padding: "15px", width: "80px" }}>
                {t("controller.participants.tableVisible")}
              </th>
              <th style={{ padding: "15px" }}>{t("controller.participants.tableCode")}</th>
              <th style={{ padding: "15px" }}>{t("controller.participants.tableAlias")}</th>
              <th style={{ padding: "15px" }}>{t("controller.participants.tableFullName")}</th>
              <th style={{ padding: "15px" }}>{t("controller.participants.tableRole")}</th>
              <th style={{ padding: "15px", textAlign: "right" }}>
                {t("controller.participants.tableActions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {[...participants]
              .sort((a, b) => (a.number || 0) - (b.number || 0))
              .map((p) => (
                <tr
                  key={p.id}
                  data-testid={`participant-row-${p.id}`}
                  style={{
                    borderBottom: "1px solid var(--color-panel-border)",
                    transition: "all 0.2s",
                    background: p.status === "eliminated" ? "rgba(0,0,0,0.2)" : "transparent",
                    opacity: p.status === "eliminated" ? 0.6 : 1,
                    filter: p.status === "eliminated" ? "grayscale(100%)" : "none",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background =
                      p.status === "eliminated" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.02)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      p.status === "eliminated" ? "rgba(0,0,0,0.2)" : "transparent")
                  }
                >
                  <td style={{ padding: "15px" }}>
                    <div
                      style={{
                        background: getParticipantColor(
                          p.metadata?.colorId,
                          p.metadata?.customColor,
                        ).bg,
                        color: getParticipantColor(p.metadata?.colorId, p.metadata?.customColor)
                          .text,
                        border: `1px solid ${getParticipantColor(p.metadata?.colorId, p.metadata?.customColor).border}`,
                        borderRadius: "4px",
                        width: "36px",
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        textShadow: getParticipantColor(
                          p.metadata?.colorId,
                          p.metadata?.customColor,
                        ).isDark
                          ? "0 1px 2px rgba(0,0,0,0.5)"
                          : "none",
                      }}
                    >
                      {p.number || "--"}
                    </div>
                  </td>
                  <td style={{ padding: "15px" }}>
                    <div
                      onMouseEnter={(e) => {
                        setHoveredParticipant(p);
                        setMousePos({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseMove={(e) => {
                        setMousePos({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseLeave={() => setHoveredParticipant(null)}
                      style={{ position: "relative", width: "fit-content" }}
                    >
                      <ParticipantBadge
                        participant={{ ...p, image: p.image || p.avatarUrl }}
                        variant="photo"
                        size="xl"
                      />
                      {p.status === "eliminated" && (
                        <div
                          style={{
                            position: "absolute",
                            inset: -2,
                            border: "2px solid #ef4444",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(0,0,0,0.5)",
                            backdropFilter: "blur(2px)",
                          }}
                        >
                          <span style={{ fontSize: "1.2rem" }}>💀</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "15px" }}>
                    <span
                      data-testid={`participant-code-${p.id}`}
                      style={{
                        fontFamily: "var(--font-mono)",
                        color: "var(--color-role-controller)",
                        fontWeight: "bold",
                        fontSize: "1rem",
                        textDecoration: p.status === "eliminated" ? "line-through" : "none",
                      }}
                    >
                      {p.code}
                    </span>
                  </td>

                  <td
                    data-testid={`participant-alias-${p.id}`}
                    style={{
                      padding: "15px",
                      fontWeight: "bold",
                      color: "rgba(255, 255, 255, 0.8)",
                      textDecoration: p.status === "eliminated" ? "line-through" : "none",
                    }}
                  >
                    {p.alias}
                  </td>
                  <td
                    data-testid={`participant-name-${p.id}`}
                    style={{
                      padding: "15px",
                      color: "var(--color-text-secondary)",
                      position: "relative",
                    }}
                  >
                    {p.name}
                    {p.status === "eliminated" && (
                      <span
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "0.6rem",
                          background: "#ef4444",
                          color: "rgba(255, 255, 255, 0.8)",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
                        }}
                      >
                        {t("controller.participants.eliminated")}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "15px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span
                        style={{
                          fontSize: "0.65rem",
                          background: "#222",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          color: "#fff",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          width: "fit-content",
                        }}
                      >
                        {p.role}
                      </span>
                      {p.groupId && (
                        <span
                          style={{
                            fontSize: "0.6rem",
                            background:
                              (externalGroups.find((g) => g.id === p.groupId)?.color || "#444") +
                              "22",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            color: externalGroups.find((g) => g.id === p.groupId)?.color || "#888",
                            fontWeight: "bold",
                            border: `1px solid ${externalGroups.find((g) => g.id === p.groupId)?.color || "#444"}44`,
                            width: "fit-content",
                          }}
                        >
                          {externalGroups.find((g) => g.id === p.groupId)?.name}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "15px", textAlign: "right" }}>
                    <div style={{ display: "inline-flex", gap: "8px" }}>
                      <button
                        data-testid={`participant-edit-btn-${p.id}`}
                        onClick={() => handleEdit(p)}
                        style={{
                          background: "transparent",
                          border: "1px solid var(--color-panel-border)",
                          color: "var(--color-text-muted)",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          transition: "all 0.2s",
                        }}
                      >
                        {t("controller.participants.edit")}
                      </button>

                      <button
                        data-testid={`participant-eliminate-btn-${p.id}`}
                        onClick={() => {
                          if (p.status !== "eliminated") {
                            setDialog({
                              isOpen: true,
                              title: t(
                                "controller.participants.disableParticipant",
                                "Disable Participant",
                              ),
                              message: t("controller.participants.disableParticipantConfirm", {
                                name: p.name,
                                defaultValue: `Are you sure you want to disable ${p.name}?`,
                              }),
                              type: "confirm",
                              onConfirm: () => {
                                socketService.emit("upsert-participant", {
                                  ...p,
                                  status: "eliminated",
                                });
                                setDialog((d) => ({ ...d, isOpen: false }));
                              },
                            });
                          } else {
                            socketService.emit("upsert-participant", { ...p, status: "available" });
                          }
                        }}
                        style={{
                          background:
                            p.status === "eliminated"
                              ? "rgba(255,255,255,0.1)"
                              : "rgba(239, 68, 68, 0.15)",
                          border: "1px solid var(--color-accent-danger)",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        title={
                          p.status === "eliminated"
                            ? t("controller.participants.restore")
                            : t("controller.participants.eliminate")
                        }
                      >
                        <span style={{ fontSize: "1rem", lineHeight: 1 }}>
                          {p.status === "eliminated" ? "♻️" : "💀"}
                        </span>
                      </button>

                      <button
                        data-testid={`participant-delete-btn-${p.id}`}
                        onClick={() => handleDelete(p.id, p.name)}
                        style={{
                          background: "transparent",
                          border: "1px solid var(--color-panel-border)",
                          color: "var(--color-text-muted)",
                          padding: "6px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {participants.length === 0 && (
        <div
          data-testid="participants-empty-state"
          style={{ padding: "40px", textAlign: "center", color: "#555", fontSize: "0.9rem" }}
        >
          {t("controller.participants.noParticipants")}
        </div>
      )}
      {/* HOVER PROFILE CARD */}
      {hoveredParticipant && (
        <div
          style={{
            position: "fixed",
            top: mousePos.y + 20,
            left: mousePos.x + 20,
            width: "450px",
            background: "rgba(20, 20, 25, 0.98)",
            backdropFilter: "blur(20px)",
            border: "1px solid var(--color-panel-border)",
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
            padding: "30px",
            zIndex: 9999,
            pointerEvents: "none",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "25px" }}>
            <div
              style={{
                width: "140px",
                height: "140px",
                aspectRatio: "1 / 1",
                flexShrink: 0,
                borderRadius: "50%",
                border: `1.5px solid ${getParticipantColor(hoveredParticipant.metadata?.colorId, hoveredParticipant.metadata?.customColor).bg}`,
                background:
                  hoveredParticipant.image || hoveredParticipant.avatarUrl
                    ? `url(${hoveredParticipant.image || hoveredParticipant.avatarUrl}) center/cover no-repeat`
                    : getParticipantColor(
                        hoveredParticipant.metadata?.colorId,
                        hoveredParticipant.metadata?.customColor,
                      ).bg,
                boxShadow: `0 0 30px ${getParticipantColor(hoveredParticipant.metadata?.colorId, hoveredParticipant.metadata?.customColor).bg}44`,
              }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  color: getParticipantColor(
                    hoveredParticipant.metadata?.colorId,
                    hoveredParticipant.metadata?.customColor,
                  ).bg,
                  fontWeight: "900",
                  fontSize: "1.8rem",
                  lineHeight: 1.1,
                  marginBottom: "5px",
                }}
              >
                {hoveredParticipant.alias}
              </div>
              <div
                style={{
                  color: "rgba(255, 255, 255, 0.8)",
                  fontSize: "1.2rem",
                  opacity: 0.9,
                  fontWeight: "bold",
                }}
              >
                {hoveredParticipant.name}
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                <span
                  style={{
                    background: "#333",
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "0.9rem",
                    border: "1px solid #444",
                  }}
                >
                  #{hoveredParticipant.number}
                </span>
                <span
                  style={{
                    background: getParticipantColor(
                      hoveredParticipant.metadata?.colorId,
                      hoveredParticipant.metadata?.customColor,
                    ).bg,
                    color: getParticipantColor(
                      hoveredParticipant.metadata?.colorId,
                      hoveredParticipant.metadata?.customColor,
                    ).text,
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                  }}
                >
                  {hoveredParticipant.role}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    background: "rgba(255,255,255, 0.1)",
                    color: getParticipantColor(
                      hoveredParticipant.metadata?.colorId,
                      hoveredParticipant.metadata?.customColor,
                    ).bg,
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    border: `1px solid ${getParticipantColor(hoveredParticipant.metadata?.colorId, hoveredParticipant.metadata?.customColor).border}`,
                  }}
                >
                  {hoveredParticipant.code}
                </span>
              </div>
            </div>
          </div>
          {hoveredParticipant.biography && (
            <div
              style={{
                background: "rgba(255,255,255,0.05)",
                padding: "15px",
                borderRadius: "6px",
                fontSize: "1rem",
                color: "#ddd",
                lineHeight: "1.5",
                fontStyle: "italic",
                borderLeft: "4px solid var(--color-role-controller)",
              }}
            >
              "{hoveredParticipant.biography}"
            </div>
          )}
        </div>
      )}
      {/* ROLE MANAGER MODAL */}
      {showRoleManager && (
        <div
          data-testid="role-manager-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.8)",
            zIndex: 10000,
            backdropFilter: "blur(5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            data-testid="role-manager-modal"
            style={{
              background: "var(--color-panel-bg)",
              padding: "25px",
              borderRadius: "6px",
              width: "350px",
              border: "1px solid var(--color-panel-border)",
              boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
            }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "1.2rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {t("controller.participants.manageRoles")}
              <button
                data-testid="role-manager-close-btn"
                onClick={() => setShowRoleManager(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#666",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </h3>
            <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
              <input
                data-testid="role-manager-name-input"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "6px",
                  border: "none",
                  background: "var(--color-input-bg)",
                  color: "rgba(255, 255, 255, 0.8)",
                }}
                placeholder={t("controller.participants.newRolePlaceholder")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newRoleName) {
                    socketService.emit("add-role", newRoleName);
                    setNewRoleName("");
                  }
                }}
              />
              <button
                data-testid="role-manager-add-btn"
                onClick={() => {
                  if (newRoleName) {
                    socketService.emit("add-role", newRoleName);
                    setNewRoleName("");
                  }
                }}
                style={{
                  background: "var(--color-accent-success)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0 15px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                {t("controller.participants.addRole")}
              </button>
            </div>
            <ul
              data-testid="role-manager-list"
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                maxHeight: "250px",
                overflowY: "auto",
              }}
            >
              {availableRoles.map((r) => (
                <li
                  key={r.id}
                  data-testid={`role-item-${r.id}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "12px",
                    borderBottom: "1px solid #333",
                    color: "#eee",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{ textTransform: "uppercase", fontWeight: "bold", fontSize: "0.9rem" }}
                  >
                    {r.name}
                  </span>
                  <button
                    data-testid={`role-delete-btn-${r.id}`}
                    onClick={() => socketService.emit("delete-role", r.id)}
                    style={{
                      background: "rgba(255,0,0,0.1)",
                      border: "1px solid var(--color-accent-danger)",
                      color: "var(--color-accent-danger)",
                      cursor: "pointer",
                      width: "24px",
                      height: "24px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.8rem",
                    }}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
            <div
              style={{ marginTop: "20px", textAlign: "center", fontSize: "0.7rem", color: "#666" }}
            >
              {t("controller.participants.rolesHelp")}
            </div>
          </div>
        </div>
      )}

      <CustomDialog
        isOpen={dialog.isOpen}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        defaultValue={dialog.defaultValue}
        onConfirm={dialog.onConfirm}
        onCancel={() => setDialog((d) => ({ ...d, isOpen: false }))}
      />

      <ImageCropperModal
        isOpen={!!cropImageSrc}
        imageSrc={cropImageSrc || ""}
        onCropComplete={(croppedBase64) => {
          setFormImage(croppedBase64);
          setFormAvatarUrl("");
          setCropImageSrc(null);
        }}
        onCancel={() => setCropImageSrc(null)}
      />
    </div>
  );
};
